import type { RefObject } from 'react';

import { useState, useEffect, useRef } from 'react';

interface IntersectionObserverInit {
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number | number[];
}

function useInViewPort<T extends HTMLElement | null>(
  ref: RefObject<T> | null,
  options?: IntersectionObserverInit,
  isActive = true,
  startValue = false
) {
  const [inView, setInView] = useState(startValue);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const unobserve = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
  };

  useEffect(() => {
    const currentRef = ref?.current;

    if (isActive) {
      observerRef.current = new IntersectionObserver(([entry]) => {
        setInView(entry.isIntersecting);
      }, options);

      if (currentRef) {
        observerRef.current.observe(currentRef);
      }
    }

    return () => {
      if (currentRef && observerRef.current) {
        observerRef.current.unobserve(currentRef);
        observerRef.current.disconnect();
      }
    };
  }, [isActive, options, ref]);

  return { inView, unobserve };
}

export { useInViewPort };
